import { format, isFuture, isPast, isToday, isTomorrow } from "date-fns";
import { Calendar, Clock, Mail } from "lucide-react";

import { Card } from "@/components/card";
import { Badge } from "@/components/ui/badge";
import { cn } from "@/lib/utils";
import { SessionPreview } from "@classview/shared-types";

import { findNextUpcomingSession } from "../utils/common";

export function NoScheduleToday({
  recurringSessions,
}: {
  recurringSessions: SessionPreview[];
}) {
  const sessionName = recurringSessions[0].name;
  const organiserName = recurringSessions[0].organisation.name;
  const nextUpcomingSession = findNextUpcomingSession(recurringSessions);

  const orderedSessions = [...recurringSessions].sort((a, b) => {
    return new Date(a.start).getTime() - new Date(b.start).getTime();
  });

  //i need only 3 past session and 3 upcoming session and one current session
  const pastSessions = orderedSessions
    .filter((session) => isPast(session.start))
    .slice(0, 3);
  const upcomingSessions = orderedSessions
    .filter((session) => isFuture(session.start))
    .slice(0, 3);
  const currentSession = orderedSessions.find((session) =>
    isToday(session.start),
  );

  const allSessions = [
    ...pastSessions,
    ...(currentSession ? [currentSession] : []),
    ...upcomingSessions,
  ];

  return (
    <div className="mx-auto max-w-md">
      <h1 className="mb-2 text-center text-2xl text-foreground">
        Session Inactive
      </h1>
      <p className="text-center text-foreground">{sessionName}</p>
      <Card className="mt-8">
        <Card.Header title="Upcoming Sessions" />

        <Card.Content>
          <ul className="space-y-2">
            {allSessions.map((session) => {
              const sessionDuration = `${format(
                session.start,
                "HH:mm",
              )} - ${format(session.end, "HH:mm")}`;
              const sessionDate = session.start;

              const formattedDate = isToday(sessionDate)
                ? "Today"
                : isTomorrow(sessionDate)
                  ? "Tomorrow"
                  : format(sessionDate, "PPP");

              if (nextUpcomingSession?.id === session.id) {
                return (
                  <li
                    key={session.id}
                    className="space-y-4 rounded-md border p-4"
                  >
                    <div className="border-l-2 border-warning pl-4">
                      <p className="text-md relative flex items-center text-foreground">
                        <Clock
                          className="mr-1 size-3 text-foreground"
                          aria-hidden={true}
                        />
                        {sessionDuration}

                        <Calendar
                          className="ml-2 mr-1 size-3 text-foreground"
                          aria-hidden={true}
                        />
                        {formattedDate}
                      </p>
                      <p className="text-md relative flex items-center text-foreground">
                        <Badge
                          variant="outline"
                          className="mr-1 flex size-5 items-center justify-center p-0 text-muted-foreground"
                        >
                          <Mail className="size-3" />
                        </Badge>
                        {organiserName}
                      </p>
                    </div>
                  </li>
                );
              }

              return (
                <li
                  key={session.id}
                  className={cn(isPast(session.start) && "opacity-50")}
                >
                  <p className="text-md relative inline-flex items-center text-muted-foreground">
                    {isPast(session.start) && (
                      <span className="absolute inset-0 top-1/2 h-[1px] bg-muted-foreground" />
                    )}
                    <span className="flex items-center">
                      <Clock
                        className="mr-1 size-3 text-card-foreground"
                        aria-hidden={true}
                      />
                      {sessionDuration}
                    </span>

                    <span className="ml-2 flex items-center">
                      <Calendar
                        className="mr-1 size-3 text-card-foreground"
                        aria-hidden={true}
                      />
                      {formattedDate}
                    </span>
                  </p>
                </li>
              );
            })}
          </ul>
        </Card.Content>
      </Card>
    </div>
  );
}
